import React from 'react';

/**
 * Measures whether an element overflows when in a "collapsed" state.
 * - Only measures while collapsed (so clientHeight reflects the cutoff).
 * - Observes size changes, image loads, and window resize.
 *
 * @param {Object} params
 * @param {boolean} params.collapsed - whether the element is currently truncated
 * @param {Array<any>} [params.deps=[]] - additional dependencies that should trigger a re-measure (e.g., content, cutoffHeight)
 * @param {boolean} [params.enableImages=true] - watch <img> load events inside the element
 * @returns {{ ref: React.MutableRefObject<any>, hasOverflow: boolean, remeasure: Function }}
 */
export function useOverflowDetector({
  collapsed,
  deps = [],
  enableImages = true
} = {}) {
  const ref = React.useRef(null);
  const [hasOverflow, setHasOverflow] = React.useState(false);

  const measure = React.useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const overflow = el.scrollHeight > el.clientHeight;
    setHasOverflow(overflow);
  }, []);

  // Re-measure on dependency changes while collapsed
  React.useEffect(() => {
    if (!collapsed) return; // only relevant when collapsed
    const id =
      typeof requestAnimationFrame !== 'undefined'
        ? requestAnimationFrame(measure)
        : null;
    if (!id) measure();
    return () => {
      if (id) cancelAnimationFrame(id);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collapsed, measure, ...deps]);

  // Attach observers/listeners while mounted
  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // ResizeObserver for element size changes
    const ro =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(() => {
            if (collapsed) measure();
          })
        : null;
    ro?.observe(el);

    // Recalculate when images inside the element finish loading
    let imgs = [];
    const onImgLoad = () => {
      if (collapsed) measure();
    };
    if (enableImages) {
      imgs = Array.from(el.querySelectorAll('img'));
      imgs.forEach(img => img.addEventListener('load', onImgLoad));
    }

    // Window resize fallback
    const onResize = () => {
      if (collapsed) measure();
    };
    window.addEventListener('resize', onResize);

    // Initial measure if needed
    if (collapsed) measure();

    return () => {
      ro?.disconnect();
      imgs.forEach(img => img.removeEventListener('load', onImgLoad));
      window.removeEventListener('resize', onResize);
    };
  }, [collapsed, enableImages, measure]);

  return { ref, hasOverflow, remeasure: measure };
}

export default useOverflowDetector;
